import { getLedgerSafeActiveLedger } from './ledgerSafeLedgerStore'
import {
  getPersistedUiPreference,
  setPersistedUiPreference
} from './uiPreferenceStorage'
import type { LedgerSafeFragmentAnchorDraft } from './ledgerSafeFragmentAnchors'

const LEDGER_SAFE_FRAGMENT_ANCHOR_STORAGE_PREFIX = 'ledgerSafeStudio:fragmentAnchors'
const LEGACY_LEDGER_SCOPE = 'legacy-active-ledger'

export interface LedgerSafeFragmentAnchorDraftStore {
  scopeKey: string
  anchors: LedgerSafeFragmentAnchorDraft[]
}

export const buildLedgerSafeFragmentAnchorStorageKey = ({
  contextId,
  ledgerId
}: {
  contextId: string
  ledgerId?: string | null
}): string => (
  `${LEDGER_SAFE_FRAGMENT_ANCHOR_STORAGE_PREFIX}::${contextId}::${ledgerId || LEGACY_LEDGER_SCOPE}`
)

const parseLedgerSafeFragmentAnchorDrafts = (value: string): LedgerSafeFragmentAnchorDraft[] => {
  if (!value) {
    return []
  }
  try {
    const parsed = JSON.parse(value)
    if (!Array.isArray(parsed)) {
      return []
    }
    return parsed.filter((candidate): candidate is LedgerSafeFragmentAnchorDraft => (
      Boolean(candidate)
      && typeof candidate.witnessId === 'string'
      && typeof candidate.unitUuid === 'string'
      && typeof candidate.unitText === 'string'
      && (candidate.previousRowId === null || typeof candidate.previousRowId === 'string')
      && (candidate.nextRowId === null || typeof candidate.nextRowId === 'string')
      && (candidate.source === 'sequence-audit' || candidate.source === 'manual')
      && typeof candidate.createdAt === 'string'
      && typeof candidate.updatedAt === 'string'
    ))
  } catch {
    return []
  }
}

export const loadLedgerSafeFragmentAnchorDrafts = async (
  contextId: string,
  ledgerId?: string | null
): Promise<LedgerSafeFragmentAnchorDraftStore> => {
  const activeLedger = ledgerId
    ? null
    : await getLedgerSafeActiveLedger(contextId).catch(() => null)
  const scopeKey = buildLedgerSafeFragmentAnchorStorageKey({
    contextId,
    ledgerId: ledgerId || activeLedger?.ledgerId
  })
  const storedValue = await getPersistedUiPreference(scopeKey)
  return {
    scopeKey,
    anchors: parseLedgerSafeFragmentAnchorDrafts(storedValue)
  }
}

export const saveLedgerSafeFragmentAnchorDrafts = async ({
  scopeKey,
  anchors
}: LedgerSafeFragmentAnchorDraftStore): Promise<boolean> => (
  setPersistedUiPreference(scopeKey, JSON.stringify(anchors))
)
